import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { ProgramListingItemGroup } from '../ProgramListing/utils';
import type { AssessmentRunsFormData } from './AssessmentRunsFormConfig';
import {
    assessmentRunsFormSchema,
    defaultAssessmentRunsFormValues,
    getFieldsToValidate,
} from './AssessmentRunsFormConfig';
import StepRenderer from './StepRenderer';
import type { FormMode } from './useAssessmentRunsFormState';
import { useAssessmentRunsFormState } from './useAssessmentRunsFormState';
import type { ProgramListItemDataFragment } from '@/gql/graphql';

export interface AssessmentRunsFormProps {
    mode: FormMode;
    program?: ProgramListItemDataFragment | null;
    selectedGroup?: ProgramListingItemGroup | null;
    onSubmit: (data: AssessmentRunsFormData) => Promise<void>;
    onClose: () => void;
}

const AssessmentRunsFormContainer = ({ mode, onSubmit, program, selectedGroup, onClose }: AssessmentRunsFormProps) => {
    const initialData = useMemo(() => {
        return selectedGroup
            ? {
                  groupId: selectedGroup.groupId,
                  groupName: selectedGroup.name,
                  assessmentRuns: selectedGroup.assessmentRuns.map(run => ({
                      id: run.id,
                      name: run.name || '',
                      startDate: new Date(run.startDate),
                      endDate: new Date(run.endDate),
                  })),
              }
            : undefined;
    }, [selectedGroup]);

    const { state, actions } = useAssessmentRunsFormState(mode, initialData);
    const form = useForm<AssessmentRunsFormData>({
        mode: 'onChange',
        resolver: zodResolver(assessmentRunsFormSchema),
        defaultValues: state.formData,
    });

    useEffect(() => {
        actions.initialize(initialData || defaultAssessmentRunsFormValues);
        form.reset(initialData);
    }, [initialData, actions, form]);

    const handleClose = useCallback(() => {
        form.reset(defaultAssessmentRunsFormValues);
        actions.reset();
        onClose();
    }, [onClose, actions, form]);

    const handleSubmit = useCallback(async () => {
        const fieldsToValidate = getFieldsToValidate();
        const isValid = await form.trigger(fieldsToValidate);
        if (!isValid) {
            return;
        }
        const formData = form.getValues();
        try {
            actions.submitStart();
            await onSubmit(formData);
            actions.submitSuccess();
            handleClose();
        } catch (error) {
            actions.submitError();
            if (error && typeof error === 'object' && 'fields' in error) {
                const fields = error.fields as { field: string; message: string }[];
                console.error('Validation errors:', fields);
                fields.forEach(err => {
                    if (err.field && err.message) {
                        form.setError(err.field as keyof AssessmentRunsFormData, {
                            type: 'manual',
                            message: err.message,
                        });
                    }
                });
            }
        }
    }, [form, onSubmit, actions, handleClose]);

    const handlePrev = () => {
        actions.prevStep();
    };

    const handleNext = async () => {
        const fieldsToValidate = getFieldsToValidate();
        const isValid = await form.trigger(fieldsToValidate);
        if (!isValid) {
            console.error('Step validation failed', form.formState.errors);
            return;
        }
        actions.nextStep();
    };

    return (
        <FormProvider {...form}>
            <StepRenderer
                step={state.currentStep}
                mode={mode}
                program={program}
                onNext={handleNext}
                onPrev={handlePrev}
                onClose={handleClose}
                onSubmit={handleSubmit}
                form={form}
            />
        </FormProvider>
    );
};

export default AssessmentRunsFormContainer;
